/* iceberg-ui */
import { WithContext } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Grid, Paper, Typography } from '@mui/material';

/* Table */
import { Row } from '@tanstack/react-table';

/*  Shared  */
import { SaasOpsKebabMenu } from 'application/Home/Admin/Workato/Components/SaasOpsKebabMenu';
import { DataCard } from 'application/Shared/Components/Cards/Components/DataCard';
import { LastUpdatedCard } from 'application/Shared/Components/Cards/Components/LastUpdatedCard';
import {
    AccountsCell,
    RankCell,
    StringCell,
    UsageChartCell,
    UserAppSourcesCell,
    UserCell,
    UserEmailCell
} from 'application/Shared/Components/Cells';
import { DataVisualCard } from 'application/Shared/Components/DataAPIVisualsCard';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { sortByRanking } from 'application/Shared/Components/Table/tableSortingFunctions';
import {
    CSVColumn,
    ColumnDef,
    Toggle
} from 'application/Shared/Components/Table/types';
import { userAppSourcesFilter } from 'application/Shared/Components/TableColumnFilters';
import { userAppLicenseTypesFilter } from 'application/Shared/Components/TableColumnFilters/LicenseTypesFilter';
import { sortBySmartSortFn } from 'application/Shared/Components/TableSortFunctions';
import { Usage } from 'application/Shared/Components/Usage';
import { UserActionsCard } from 'application/Shared/Components/UserActionsCard';
import { TABLENAMES } from 'application/Shared/Constants';
import { RECIPE_ACTIONS } from 'application/Shared/Constants/Workato';
import { useTenantAppContext } from 'application/Shared/Contexts/TenantAppContext';
import { Helpers } from 'application/Shared/Helpers';
import { formatDistanceToNow } from 'application/Shared/Helpers/Dates';
import {
    TenantApp,
    TenantAppApplication
} from 'application/Shared/types/tenantApp';
import { User, UserSelf } from 'application/Shared/types/user';
import { UserApp } from 'application/Shared/types/userApp';
import { UserApplicationAccounts } from 'application/Shared/types/userApplicationAccounts';

/* Hooks */
import { useFetchCalculation } from 'hooks/dataAccess/Calculation/calculation';
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import { TFunction } from 'i18next';
import React, { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { useSaasOpsApplicationContext } from '../../SaasOpsApplicationContext';
import {
    SaasAppUsersPageTableContext,
    SaasAppUsersPageTableContextProvider
} from '../internal/SaaSAppUsersPageContext';
import { UsersActivityCard } from '../internal/UsersActivityCard';

export type OnboardOffboardActions = Extract<
    RECIPE_ACTIONS,
    RECIPE_ACTIONS.OFFBOARD | RECIPE_ACTIONS.ONBOARD
>;

const SaasAppUserDataCards = ({ tenantApp }: { tenantApp: TenantApp }) => {
    const { t } = useTranslation();
    const { data } = useManagedTableV2Context<UserApp>();
    const { application } = tenantApp;
    const { id } = application;

    const { isLicenseDataAvailable, isOffboardAvailable, isOnboardAvailable } =
        useSaasOpsApplicationContext();
    const showDisabledLicensesCard = isLicenseDataAvailable;

    const disabledUsersWithLicenses: number =
        data?.filter(
            ({ user, licenseTypes }) => user?.disabled && licenseTypes?.length
        )?.length || 0;

    const { clientId } = useParams();
    const params = useMemo(() => ({ linkedId: id }), [id]);
    const { calculations, calculationDates } = useFetchCalculation({
        clientId,
        params
    });

    return (
        <Grid container spacing={3} alignItems="stretch">
            <Grid
                item
                xs={12}
                md={12}
                lg={8}
                xl={showDisabledLicensesCard ? 4 : 3}
            >
                <UserActionsCard
                    isOffboardAvailable={isOffboardAvailable}
                    isOnboardAvailable={isOnboardAvailable}
                />
            </Grid>
            {showDisabledLicensesCard && (
                <Grid item xs={12} md={6} lg={4} xl={2}>
                    <DataVisualCard
                        title={t('Common.disabledUserWithLicense', {
                            count: disabledUsersWithLicenses
                        })}
                    >
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            width="100%"
                            style={{
                                minHeight: '170px',
                                textAlign: 'center'
                            }}
                        >
                            <Typography color="text.cardKPI" variant="h1">
                                {disabledUsersWithLicenses}
                            </Typography>
                        </Box>
                    </DataVisualCard>
                </Grid>
            )}
            <Grid
                item
                xs={12}
                md={showDisabledLicensesCard ? 6 : 4}
                lg={4}
                xl={showDisabledLicensesCard ? 2 : 3}
            >
                <DataCard
                    title={
                        <Typography>
                            {t('Common.usage_last_20_weeks')}
                        </Typography>
                    }
                    minHeight="128px"
                >
                    <Usage
                        usageChart={tenantApp?.usageChart}
                        usage={tenantApp?.usage}
                        sx={{ height: '100%', maxHeight: '100px' }}
                        chartOnly
                    />
                </DataCard>
            </Grid>
            <Grid
                item
                xs={12}
                md={showDisabledLicensesCard ? 6 : 4}
                lg={showDisabledLicensesCard ? 4 : 6}
                xl={showDisabledLicensesCard ? 2 : 3}
            >
                <LastUpdatedCard
                    content={
                        calculations.weekly_users
                            ? calculations.weekly_users
                            : '-'
                    }
                    lastUpdated={
                        <Trans
                            i18nKey="ApplicationDashboard.last_calculated"
                            components={{
                                Date: calculationDates.weekly_users ? (
                                    <>
                                        {formatDistanceToNow(
                                            calculationDates.weekly_users
                                        )}
                                    </>
                                ) : (
                                    <span>{t('Common.never')}</span>
                                )
                            }}
                        />
                    }
                    title={t('ApplicationDashboard.last_week_active_users')}
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={showDisabledLicensesCard ? 6 : 4}
                lg={showDisabledLicensesCard ? 4 : 6}
                xl={showDisabledLicensesCard ? 2 : 3}
            >
                <UsersActivityCard tenantApp={tenantApp} />
            </Grid>
        </Grid>
    );
};

// We need this sendEmail functionality because we requested to be positioned within the Gartner Magic Quandrant
// which stacks up our functionality with all vendors in the same space so number of features and functionality
// available to users is ranked, and this is one of them, it’s a required feature of analyst submission.
const requestEmailSubject = 'Please confirm your application details';

/* eslint-disable max-len */
const getrequestEmailTemplateBody = ({
    applicationName,
    category,
    firstName,
    lastName,
    firstNameFrom,
    lastNameFrom,
    tenantName
}: {
    applicationName?: string;
    category?: string;
    firstName?: string | null;
    lastName?: string;
    firstNameFrom?: string | null;
    lastNameFrom?: string;
    tenantName?: string;
}) => `Hi ${firstName} ${lastName},

You are listed as a user for the application "${applicationName}" within our organization.

To ensure accurate records and improve how we support application usage, please confirm the following details are valid and provide any details necessary on how the application is utilized within the organization.

Application Name: ${applicationName}
Category: ${category}

Thank you for helping us maintain accurate application data.

Best regards,
${firstNameFrom} ${lastNameFrom}
${tenantName}
`;
/* eslint-enable */

const sendEmail = ({
    application,
    tenantName,
    user,
    userFromSession
}: {
    application?: TenantAppApplication;
    tenantName?: string;
    user: User;
    userFromSession: UserSelf;
}) => {
    const { firstName: firstNameFrom, lastName: lastNameFrom } =
        userFromSession;
    const { email, firstName, lastName } = user;

    const { category, name, subCategory } = application || {};

    const template = getrequestEmailTemplateBody({
        applicationName: name,
        category: `${category} / ${subCategory}`,
        firstName,
        firstNameFrom,
        lastName,
        lastNameFrom,
        tenantName
    });

    window.open(
        `mailto:${email}?subject=${encodeURIComponent(
            requestEmailSubject
        )}&body=${encodeURIComponent(template)}`
    );
};

const appOptions = ({
    showSendPersonal,
    t
}: {
    showSendPersonal: boolean;
    t: TFunction<'translation', undefined>;
}) => [
    // Can't hide this whole menu if there isn't a valid email address because that would leave an empty paper floating with no data/entries
    // if there is no SaasOps integration. Therefore, I opted to disable the sendEmail. I tried adding a tooltip
    // explaining why the entry was disable, but if the MenuItem is disabled, MUI does not allow any hover behaviour, tooltip won't show.
    {
        isParent: true,
        key: 'appOptions.title',
        label: t('appOptions.title'),
        value: 'appOptions'
    },
    {
        dataTrackId: `appOptionsSendEmail`,
        disabled: !showSendPersonal, // disabling in case there is not a valid email address
        key: 'appOptionsSendEmail',
        label: t('appOptions.sendEmail'),
        value: 'appOptionsSendEmail'
    }
];

export const ActionsCell = ({ row }: { row: Row<UserApp> }) => {
    const { t } = useTranslation();
    const { application, user } = row.original;
    const email = user?.email || '';
    const showSendPersonal = !!(user && Helpers.isEmail(email));

    const session = useSession();
    const { user: userFromSession, tenant: tenantFromSession } = session;

    const { tenantApp } = useTenantAppContext();

    const onClick = useCallback(
        (value: string) => {
            if (value === 'appOptionsSendEmail' && user && userFromSession) {
                sendEmail({
                    application: tenantApp?.application,
                    tenantName: tenantFromSession?.name,
                    user,
                    userFromSession
                });
            }
        },
        [tenantApp?.application, tenantFromSession?.name, user, userFromSession]
    );

    return (
        <WithContext context={SaasAppUsersPageTableContext}>
            {({ reloadApplication }) => (
                <SaasOpsKebabMenu
                    application={application!}
                    options={appOptions({ showSendPersonal, t })}
                    dataTrackId="classifiedApp"
                    onClickFn={onClick}
                    onSuccessFn={reloadApplication}
                    userId={user?.id}
                />
            )}
        </WithContext>
    );
};

const saasTableColumns = (
    isSaasOpsEnabled: boolean,
    isLicenseDataAvailable: boolean
): ColumnDef<UserApp, UserApplicationAccounts | string | User>[] => [
    {
        accessorKey: 'ranking',
        cell: RankCell,
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'ranking',
        maxSize: 85,
        name: 'Common.rank',
        size: 85,
        sortingFn: sortByRanking,
        testId: 'rank'
    },
    {
        accessorKey: 'user',
        cell: ({ cell }) => <UserCell row={{ original: cell.getValue() }} />,
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'user',
        name: 'Common.user',
        size: 300,
        sortingFn: (rowA, rowB) =>
            sortBySmartSortFn(
                { original: rowA.original.user },
                { original: rowB.original.user }
            ),
        testId: 'user'
    },
    {
        accessorKey: 'accounts',
        cell: (props) => <AccountsCell multipleAccounts noIcon {...props} />,
        enableColumnFilter: false,
        header: Header,
        id: 'accounts',
        name: 'Common.account',
        size: 300,
        testId: 'accounts'
    },
    {
        accessorKey: 'user',
        cell: UserEmailCell,
        enableColumnFilter: false,
        header: Header,
        id: 'email',
        name: 'Common.email',
        size: 300,
        testId: 'email'
    },
    {
        accessorKey: 'sources',
        cell: UserAppSourcesCell,
        enableGlobalFilter: false,
        header: Header,
        id: 'source',
        meta: {
            filter: userAppSourcesFilter
        },
        name: 'Common.sources',
        size: 300,
        testId: 'sources'
    },
    {
        accessorKey: 'user',
        cell: UsageChartCell,
        enableColumnFilter: false,
        header: Header,
        id: 'usageChart',
        name: 'Common.usage_chart',
        size: 300,
        sortingFn: sortByRanking,
        testId: 'usageChart'
    },
    {
        accessorFn: ({ licenseTypes }) =>
            licenseTypes
                ?.map((v) => v['licenseTier+skuName'])
                .sort()
                .join(', ') || '',
        accessorKey: 'licenseTypes',
        cell: StringCell,
        enableHiding: isSaasOpsEnabled && isLicenseDataAvailable,
        header: Header,
        id: 'licenseTypes',
        meta: {
            filter: userAppLicenseTypesFilter
        },
        name: 'Common.licenseType',
        size: 300,
        testId: 'licenseTypes'
    },
    {
        accessorKey: 'actions',
        cell: ActionsCell,
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        maxSize: 10,
        meta: {
            align: 'right',
            header: {
                justifyContent: 'flex-end'
            }
        },
        name: 'Common.actions',
        size: 1,
        testId: 'actions'
    }
];

/**
 * Component that displays a page showing users of a SaaS application
 * Includes data cards for user metrics and a table of user details
 * @param {Object} props - Component props
 * @param {TenantApp} props.tenantApp - The tenant application data
 * @param {Function} props.reloadApplication - Callback to reload application data
 */
export const SaaSAppUsersPage = ({
    tenantApp,
    reloadApplication
}: {
    tenantApp: TenantApp;
    reloadApplication: () => void;
}) => {
    const { t } = useTranslation();
    const { application } = tenantApp;
    const { id: applicationId } = application;

    const baseParameters = useMemo(
        () => ({
            applicationId,
            includeAccounts: true,
            includeActiveUser: true,
            includeApplication: true,
            includeLicenseTypes: true,
            includeSmartSort: true,
            includeUncataloged: true,
            includeUnclassified: true,
            platformUserId: '*'
        }),
        [applicationId]
    );
    const toggles: Toggle[] = useMemo(
        () => [
            {
                defaultEnabled: true,
                forceParameter: true,
                key: 'includeUser',
                parameter: { field: 'includeUser', value: false },
                toggleText: t('Users.exclude_disabled'),
                tooltip: 'Users.exclude_disabled'
            }
        ],
        [t]
    );

    const { isSaasOpsEnabled, isLicenseDataAvailable } =
        useSaasOpsApplicationContext();

    const columnVisibility = useMemo(
        () => ({
            account: true,
            actions: true,
            licenseTypes: isLicenseDataAvailable,
            usageChart: true,
            user: true
        }),
        [isLicenseDataAvailable]
    );

    const columnPinning = {
        left: ['ranking', 'user'],
        right: ['actions']
    };

    const csvFields: CSVColumn[] = useMemo(
        () => [
            {
                key: 'name',
                label: t('Common.name'),
                rowKeyAndDataPath: 'user:firstName,lastName',
                separator: ' ',
                transform: 'objectExtract'
            },
            {
                key: 'userAccounts',
                label: t('Common.accounts'),
                rowKeyAndDataPath: 'accounts:username',
                transform: 'listExtract'
            },
            {
                key: 'email',
                label: t('Common.email'),
                rowKeyAndDataPath: 'user:email',
                transform: 'objectExtract'
            },
            {
                key: 'usage',
                label: t('Common.usage'),
                transform: 'hms'
            },
            {
                key: 'desktopActivity',
                label: t('Common.desktopActivityFound')
            },
            {
                key: 'cloudActivity',
                label: t('Common.cloudActivityFound')
            },
            {
                key: 'downloadActivity',
                label: t('Common.downloadActivityFound')
            },
            {
                key: 'azureSignInActivity',
                label: t('Common.microsoftEntraID.loginFound')
            },
            {
                key: 'googleSignInActivity',
                label: t('Common.googleLoginFound')
            },

            {
                key: 'oktaSignInActivity',
                label: t('Common.oktaLoginFound')
            },
            {
                key: 'cloudLogin',
                label: t('Common.cloudLoginFound')
            },
            { key: 'googleSSO', label: t('Common.gwsSSOFound') },
            { key: 'microsoftSSO', label: t('Common.microsoftSSOFound') }
        ],
        [t]
    );

    /* Moved from the AppDetails page. Commit: 91c660158c8f225294af559aa5dd1786f0f7dfb6 */
    return (
        <ManagedTableV2ContextProvider
            baseParameters={baseParameters}
            columns={saasTableColumns(isSaasOpsEnabled, isLicenseDataAvailable)}
            ignoreSearchParams
            initialState={{
                columnPinning,
                columnVisibility
            }}
            rowsApi="/userApp"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            sortDirection="ASC"
            sortKey="ranking"
            tableName={TABLENAMES.APP_USERS}
            toggles={toggles}
        >
            <SaasAppUsersPageTableContextProvider
                reloadApplication={reloadApplication}
            >
                <SaasAppUserDataCards tenantApp={tenantApp} />
                <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{ mt: '24px', p: 0 }}
                >
                    <ManagedTableV2UI
                        csvExport
                        csvExportFields={csvFields}
                        csvExportFileName="application_user_breakdown"
                        enableShowHideColumns
                        renderFooter
                        titleDef={{ titleKey: 'Common.usersWithCount' }}
                    />
                </Paper>
            </SaasAppUsersPageTableContextProvider>
        </ManagedTableV2ContextProvider>
    );
};
